/**
 * AddAdjustmentForm - Form for creating or editing an adjustment
 */

import React, { useState, useEffect } from 'react';
import { X, Loader2, DollarSign, Percent, Calendar, AlertCircle } from 'lucide-react';
import type { ForecastAdjustment, AdjustmentType } from '../../api';
// eslint-disable-next-line import/no-relative-packages
import { currencySymbol } from '../../../../utils/currency';

interface AddAdjustmentFormProps {
  adjustment?: ForecastAdjustment;  // If provided, we're editing
  onSubmit: (values: {
    adjustmentType: AdjustmentType;
    value: number;
    description: string;
    startMonth: string;
    endMonth: string;
    expiresAt?: string;
  }) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  selectedYear: number;
  currency?: string | null;
}

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export function AddAdjustmentForm({
  adjustment,
  onSubmit,
  onCancel,
  isSubmitting,
  selectedYear,
  currency,
}: AddAdjustmentFormProps) {
  const isEditing = !!adjustment;

  // Parse existing adjustment dates from ISO strings directly to avoid timezone offset issues.
  // new Date().getUTCMonth() can shift months when UTC midnight falls on the previous day in local time.
  const parseMonth = (dateStr?: string): number => {
    if (!dateStr) return new Date().getMonth() + 1;
    // Parse month directly from ISO string "YYYY-MM-DD..." to avoid timezone issues
    return parseInt(dateStr.substring(5, 7), 10);
  };

  const parseYear = (dateStr?: string): number => {
    if (!dateStr) return selectedYear;
    // Parse year directly from ISO string "YYYY-MM-DD..." to avoid timezone issues
    return parseInt(dateStr.substring(0, 4), 10);
  };

  // Form state
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>(
    adjustment?.adjustmentType || 'FLAT'
  );
  const [value, setValue] = useState<string>(
    adjustment?.value?.toString() || ''
  );
  const [description, setDescription] = useState(adjustment?.description || '');
  const [startMonth, setStartMonth] = useState(parseMonth(adjustment?.startMonth));
  const [endMonth, setEndMonth] = useState(parseMonth(adjustment?.endMonth));
  const [hasExpiration, setHasExpiration] = useState(!!adjustment?.expiresAt);
  const [expiresAt, setExpiresAt] = useState(
    adjustment?.expiresAt ? adjustment.expiresAt.split('T')[0] : ''
  );
  const [error, setError] = useState<string | null>(null);

  // Build month date string (first of month)
  const buildMonthDate = (month: number, year: number): string => {
    return `${year}-${String(month).padStart(2, '0')}-01`;
  };

  // Validate form
  const validate = (): boolean => {
    setError(null);

    if (!value || isNaN(parseFloat(value.replace(/,/g, '')))) {
      setError('Please enter a valid amount');
      return false;
    }

    if (startMonth > endMonth) {
      setError('Start month must be before or equal to end month');
      return false;
    }

    if (hasExpiration && !expiresAt) {
      setError('Please enter an expiration date or disable expiration');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await onSubmit({
        adjustmentType,
        value: parseFloat(value.replace(/,/g, '')),
        description,
        startMonth: buildMonthDate(startMonth, selectedYear),
        endMonth: buildMonthDate(endMonth, selectedYear),
        expiresAt: hasExpiration && expiresAt ? expiresAt : undefined,
      });
    } catch (err) {
      // Error is handled by parent
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-700">
          {isEditing ? 'Edit Adjustment' : 'New Adjustment'}
        </h4>
        <button
          type="button"
          onClick={onCancel}
          className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {/* Type Toggle */}
      <div>
        <label className="block text-xs text-slate-500 mb-1.5">Type</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setAdjustmentType('FLAT')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
              adjustmentType === 'FLAT'
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            <DollarSign size={14} />
            Flat Amount
          </button>
          <button
            type="button"
            onClick={() => setAdjustmentType('PERCENT')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
              adjustmentType === 'PERCENT'
                ? 'bg-purple-50 border-purple-300 text-purple-700'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            <Percent size={14} />
            Percentage
          </button>
        </div>
      </div>

      {/* Value Input */}
      <div>
        <label className="block text-xs text-slate-500 mb-1.5">
          {adjustmentType === 'FLAT' ? 'Amount' : 'Percentage'}
        </label>
        <div className="relative">
          {adjustmentType === 'FLAT' && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{currencySymbol(currency)}</span>
          )}
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={adjustmentType === 'FLAT' ? '100000' : '5'}
            className={`w-full py-2 border border-slate-200 rounded-lg text-sm focus:outline-none ${
              adjustmentType === 'FLAT' ? 'pl-7 pr-3' : 'px-3 pr-7'
            }`}
            onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--lodge-orange-400) 20%, transparent)'; e.currentTarget.style.borderColor = 'var(--lodge-orange-400)'; }}
            onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
          />
          {adjustmentType === 'PERCENT' && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-400">
          {adjustmentType === 'FLAT'
            ? 'Enter positive to add, negative to subtract'
            : 'Enter positive to increase, negative to decrease'}
        </p>
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs text-slate-500 mb-1.5">
          Description <span className="text-slate-300">(optional)</span>
        </label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., Convention group booking"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none"
          onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--lodge-orange-400) 20%, transparent)'; e.currentTarget.style.borderColor = 'var(--lodge-orange-400)'; }}
          onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
        />
      </div>

      {/* Month Range */}
      <div>
        <label className="block text-xs text-slate-500 mb-1.5">Apply to Months</label>
        <div className="flex items-center gap-2">
          <select
            value={startMonth}
            onChange={(e) => setStartMonth(parseInt(e.target.value))}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none"
            onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--lodge-orange-400) 20%, transparent)'; e.currentTarget.style.borderColor = 'var(--lodge-orange-400)'; }}
            onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <span className="text-slate-400">to</span>
          <select
            value={endMonth}
            onChange={(e) => setEndMonth(parseInt(e.target.value))}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none"
            onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--lodge-orange-400) 20%, transparent)'; e.currentTarget.style.borderColor = 'var(--lodge-orange-400)'; }}
            onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Adjustment applies to {selectedYear}
        </p>
      </div>

      {/* Expiration */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <input
            type="checkbox"
            id="hasExpiration"
            checked={hasExpiration}
            onChange={(e) => setHasExpiration(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300"
            style={{ accentColor: 'var(--lodge-orange-400)' }}
          />
          <label htmlFor="hasExpiration" className="text-xs text-slate-500">
            Set expiration date
          </label>
        </div>
        {hasExpiration && (
          <div className="mt-2">
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none"
              onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--lodge-orange-400) 20%, transparent)'; e.currentTarget.style.borderColor = 'var(--lodge-orange-400)'; }}
              onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
            />
            <p className="mt-1 text-xs text-slate-400">
              Adjustment stops applying after this date, even for months in range
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 px-4 py-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !value}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
        >
          {isSubmitting && <Loader2 size={14} className="animate-spin" />}
          {isEditing ? 'Update' : 'Add'} Adjustment
        </button>
      </div>
    </form>
  );
}

export default AddAdjustmentForm;
